/**
 * Sales Insights dashboard (/pc/insights): the rep's accounts, with the
 * signal for each. Fetches the book once and hands each section the part it
 * shows. The app's header and sidebar keep their normal look; everything
 * inside InsightsTheme matches the v0.1 mockup.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/layout";
import { useAuthenticatedQuery } from "@/hooks/use-api";
import { useSalesInsightsStatus } from "@/hooks/use-sales-insights";
import { PC_INSIGHTS_QUERY_KEY } from "@/requests/pc-insights";
import {
  BOOK_SCOPES,
  type BookScope,
  type SalesInsightsBookResponse,
} from "@onsemble/shared/types/sales-insights";
import { InsightsTheme } from "../InsightsTheme";
import { BrandHeader } from "../shared/BrandHeader";
import type { Period } from "../shared/PeriodTabs";
import { Summary } from "./sections/Summary";
import { Controls } from "./sections/Controls";
import { AccountList } from "./sections/AccountList";

export default function InsightsDashboard() {
  const { t } = useTranslation();
  // A marker for another PC: say so instead of asking the server for a 403
  const status = useSalesInsightsStatus();
  // The All | Assigned | Unassigned choice the user clicked; null until they click one, and resets when the page reloads
  const [picked, setPicked] = useState<BookScope | null>(null);
  // Account search text
  const [query, setQuery] = useState("");
  // Month to date or year to date, from the MTD | YTD picker; starts on month
  const [period, setPeriod] = useState<Period>("month");

  const {
    data: book,
    isLoading,
    error,
  } = useAuthenticatedQuery<SalesInsightsBookResponse>([...PC_INSIGHTS_QUERY_KEY], {
    enabled: status.kind === "ok",
  });
  // The choices the server sent totals for: just Assigned for members, all three for admins
  const scopes = book ? BOOK_SCOPES.filter((s) => book.book[s]) : [];
  // The choice shown: the clicked one, else the first one (All for admins, Assigned for members)
  const scope = picked ?? scopes[0] ?? "assigned";

  return (
    <Layout
      headerContent={<span className="font-semibold">{t("pages.pcInsights.title")}</span>}
    >
      {status.kind === "wrong-pc" ? (
        // Marker is for another PC, or this org has no PC id
        <p className="text-muted-foreground py-6 text-center">
          {t("pages.pcInsights.notFoundInOrg")}
        </p>
      ) : isLoading ? (
        <div className="h-10 bg-muted rounded animate-pulse" />
      ) : error || !book ? (
        <p className="text-destructive py-6 text-center">{t("pages.pcInsights.loadFailed")}</p>
      ) : book.accounts.length === 0 ? (
        // Nothing loaded for this PC yet
        <p className="text-muted-foreground py-6 text-center">{t("pages.pcInsights.empty")}</p>
      ) : scopes.length === 1 && book.book.assigned.count === 0 ? (
        // A member with no accounts assigned to their salesperson ID
        <p className="text-muted-foreground py-6 text-center">
          {t("pages.pcInsights.notFoundInOrg")}
        </p>
      ) : (
        <InsightsTheme>
          {/* Wordmark and product name; how fresh the data is on the right */}
          <BrandHeader
            asOf={book.asOf}
            repName={book.rep.osrName ?? book.rep.osrId}
            profitCenters={book.profitCenters}
          />
          {/* The headline numbers */}
          <div className="mt-8">
            <Summary
              book={book}
              scope={scope}
              period={period}
              onPeriodChange={setPeriod}
            />
          </div>
          {/* The list: heading and search, then the accounts */}
          <div className="mt-10">
            <Controls
              scope={scope}
              scopes={scopes}
              onScopeChange={setPicked}
              query={query}
              onQueryChange={setQuery}
            />
            <AccountList
              accounts={book.accounts}
              scope={scope}
              scopes={scopes}
              query={query}
              period={period}
              onShowAll={() => setPicked("all")}
              onClearQuery={() => setQuery("")}
            />
          </div>
        </InsightsTheme>
      )}
    </Layout>
  );
}
